/**
 * The rail's order (Console v2 §3.1, Discord's `guild_positions`): the
 * official server always first, then the order you dragged, then the rest
 * in join order (`railGuildIds`). A move shows at once and is saved to your
 * account; a refusal puts the old order back.
 */
import type { Snowflake } from '@neosocial/protocol';
import { tr } from '../i18n';
import { api } from '../lib/api';
import { updateData, useData } from '../store/data';
import { officialGuildId, railGuildIds } from '../store/selectors';
import { announce, toast } from '../ui/overlay';

/** The rail as it would be with `guildId` at `index` (0 = the first after the official server). */
export function movedOrder(
  order: readonly Snowflake[],
  guildId: Snowflake,
  index: number,
): Snowflake[] {
  const rest = order.filter((id) => id !== guildId);
  const at = Math.max(0, Math.min(index, rest.length));
  return [...rest.slice(0, at), guildId, ...rest.slice(at)];
}

function setPositions(positions: Snowflake[]): void {
  updateData((s) =>
    s.settings ? { ...s, settings: { ...s.settings, guild_positions: positions } } : s,
  );
}

/**
 * Moves a server you made or joined to `index` among the movable ones (the
 * official server never moves) and saves it.
 */
export async function moveGuild(guildId: Snowflake, index: number): Promise<void> {
  const d = useData.getState();
  const official = officialGuildId(d);
  if (guildId === official) return;
  const movable = railGuildIds(d).filter((id) => id !== official);
  const next = movedOrder(movable, guildId, index);
  if (next.join() === movable.join()) return;
  const before = d.settings?.guild_positions ?? [];
  setPositions(next);
  const { t } = tr();
  announce(
    t('sv.rail.moved', { name: d.guilds[guildId]?.name ?? '', n: next.indexOf(guildId) + 2 }),
  );
  try {
    await api.me.patchSettings({ guild_positions: next });
  } catch {
    setPositions(before);
    toast(t('sv.rail.saveFailed'), { icon: 'alert', tone: 'warn' });
  }
}
